/* RAISE A JOB — a pump company books the concrete it needs straight into one of its
   connected suppliers. The supplier then just taps SEND TRUCK on their side. */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ClipboardPlus, Loader2, Trash2, Truck } from "lucide-react";
import { api } from "@/lib/api";

const field = "w-full bg-[#0B0D10] border border-[#2C313A] focus:border-[#38BDF8] outline-none text-white px-3 py-2.5 text-sm";
const today = () => new Date().toISOString().slice(0, 10);
const blank = () => ({ org_id: "", date: today(), time_required: "", site_name: "", site_address: "", quantity_m3: "", mix_code: "", pump_unit_id: "", notes: "" });

export default function PartnerJobs() {
  const [suppliers, setSuppliers] = useState([]);
  const [units, setUnits] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [f, setF] = useState(blank());
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [d, u, j] = await Promise.all([
        api.get("/partner/dashboard"), api.get("/partner/units"), api.get("/partner/jobs"),
      ]);
      const sup = (d.data.suppliers || []).filter((s) => s.sharing);
      setSuppliers(sup);
      setUnits(u.data.units || []);
      setJobs(j.data.jobs || []);
      setF((p) => ({ ...p, org_id: p.org_id || (sup[0]?.org_id ?? "") }));
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load your suppliers"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.org_id) return toast.error("Pick the supplier you want the concrete from");
    if (!f.site_name.trim()) return toast.error("Where's the pour? Give the site a name");
    setBusy(true);
    try {
      await api.post("/partner/jobs", { ...f, quantity_m3: parseFloat(f.quantity_m3 || 0) || 0 });
      toast.success("Job sent to your supplier");
      setF({ ...blank(), org_id: f.org_id, date: f.date });
      load();
    } catch (ex) { toast.error(ex.response?.data?.detail || "Could not raise the job"); }
    finally { setBusy(false); }
  };

  const cancel = async (j) => {
    if (!window.confirm(`Cancel the ${j.time_required || ""} pour at ${j.site_name}?`)) return;
    try { await api.delete(`/partner/jobs/${j.id}`); toast.success("Cancelled"); load(); }
    catch (ex) { toast.error(ex.response?.data?.detail || "Could not cancel"); }
  };

  if (loading) return <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 text-[#38BDF8] animate-spin" /></div>;

  return (
    <div className="p-4 lg:p-8 space-y-6 max-w-3xl" data-testid="partner-jobs-page">
      <div>
        <div className="text-[10px] tracking-[0.25em] text-[#8B909A]">BOOK CONCRETE</div>
        <h1 className="heading text-2xl sm:text-3xl font-black uppercase text-white">Raise a <span className="text-[#38BDF8]">Job</span></h1>
        <p className="text-[13px] text-[#A1A6B0] mt-1">Book the pour with your supplier. They'll see it on their screen and send a truck — you'll watch it come in on the map.</p>
      </div>

      {suppliers.length === 0 ? (
        <div className="border border-dashed border-[#2C313A] p-8 text-center" data-testid="partner-jobs-no-suppliers">
          <Truck className="w-8 h-8 mx-auto text-[#38BDF8]" />
          <div className="heading text-lg font-black uppercase text-white mt-3">No connected suppliers yet</div>
          <p className="text-[13px] text-[#A1A6B0] mt-2">Send your concrete plant an invite from <span className="text-white">Invite Suppliers</span> first.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="bg-[#12151A] border border-[#22262E] p-5 space-y-3" data-testid="partner-job-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <label><span className="block text-[10px] tracking-widest uppercase text-[#8B909A] mb-1">Supplier *</span>
              <select data-testid="pj-supplier" value={f.org_id} onChange={(e) => setF({ ...f, org_id: e.target.value })} className={field}>
                {suppliers.map((s) => <option key={s.org_id} value={s.org_id}>{s.name}</option>)}
              </select></label>
            <label><span className="block text-[10px] tracking-widest uppercase text-[#8B909A] mb-1">My pump (optional)</span>
              <select data-testid="pj-unit" value={f.pump_unit_id} onChange={(e) => setF({ ...f, pump_unit_id: e.target.value })} className={field}>
                <option value="">— not set —</option>
                {units.map((u) => <option key={u.id} value={u.id}>{u.name}{u.operator_name ? ` · ${u.operator_name}` : ""}</option>)}
              </select></label>
            <label><span className="block text-[10px] tracking-widest uppercase text-[#8B909A] mb-1">Date *</span>
              <input type="date" data-testid="pj-date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={field} /></label>
            <label><span className="block text-[10px] tracking-widest uppercase text-[#8B909A] mb-1">Time on site</span>
              <input type="time" data-testid="pj-time" value={f.time_required} onChange={(e) => setF({ ...f, time_required: e.target.value })} className={field} /></label>
            <label className="sm:col-span-2"><span className="block text-[10px] tracking-widest uppercase text-[#8B909A] mb-1">Site name *</span>
              <input data-testid="pj-site" value={f.site_name} onChange={(e) => setF({ ...f, site_name: e.target.value })} placeholder="e.g. 14 Jackson St slab" className={field} /></label>
            <label className="sm:col-span-2"><span className="block text-[10px] tracking-widest uppercase text-[#8B909A] mb-1">Site address</span>
              <input data-testid="pj-address" value={f.site_address} onChange={(e) => setF({ ...f, site_address: e.target.value })} placeholder="Street, suburb — so the driver can navigate" className={field} /></label>
            <label><span className="block text-[10px] tracking-widest uppercase text-[#8B909A] mb-1">Volume (m³)</span>
              <input type="number" step="0.5" min="0" data-testid="pj-qty" value={f.quantity_m3} onChange={(e) => setF({ ...f, quantity_m3: e.target.value })} className={field} /></label>
            <label><span className="block text-[10px] tracking-widest uppercase text-[#8B909A] mb-1">Mix</span>
              <input data-testid="pj-mix" value={f.mix_code} onChange={(e) => setF({ ...f, mix_code: e.target.value })} placeholder="e.g. 25MPA 80mm" className={field} /></label>
            <label className="sm:col-span-2"><span className="block text-[10px] tracking-widest uppercase text-[#8B909A] mb-1">Notes for the driver</span>
              <input data-testid="pj-notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="Access, gate code, who to ring" className={field} /></label>
          </div>
          <button type="submit" disabled={busy} data-testid="pj-submit"
            className="w-full bg-[#38BDF8] text-[#0B0D10] hover:bg-[#7DD3FC] font-black uppercase tracking-widest text-sm py-3 disabled:opacity-50 inline-flex items-center justify-center gap-2">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardPlus className="w-4 h-4" />} Send job to supplier
          </button>
        </form>
      )}

      <section data-testid="partner-jobs-today">
        <h2 className="heading text-base md:text-lg font-black uppercase text-white">Jobs I've booked today</h2>
        {jobs.length === 0 ? (
          <div className="mt-2 text-[13px] text-[#A1A6B0]">Nothing booked yet today.</div>
        ) : (
          <div className="mt-3 space-y-2">
            {jobs.map((j) => (
              <div key={j.id} className="border border-[#22262E] bg-[#12151A] p-4 flex items-start gap-3" data-testid={`pj-row-${j.id}`}>
                <div className="mono text-base font-black text-[#38BDF8] shrink-0">{j.time_required || "--:--"}</div>
                <div className="min-w-0">
                  <div className="font-black uppercase text-white truncate">{j.site_name}</div>
                  <div className="mono text-[11px] text-[#A1A6B0] truncate">{j.supplier}{j.quantity_m3 ? ` · ${j.quantity_m3} m³` : ""}{j.mix_code ? ` · ${j.mix_code}` : ""}</div>
                  <div className="mono text-[11px] text-[#8B909A] mt-1">
                    {j.trucks.length ? j.trucks.map((t) => `${t.number} (${t.status})`).join(", ") : "waiting on a truck"}
                  </div>
                </div>
                {j.trucks.length === 0 && (
                  <button onClick={() => cancel(j)} data-testid={`pj-cancel-${j.id}`} className="ml-auto p-2 border border-[#2C313A] text-[#8B909A] hover:border-red-500 hover:text-red-400 shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
